import React from 'react';
import { Switch, Route, useHistory } from 'react-router-dom';

import { uniqueId } from '@ast/magma/inner/utils/uniqueid';

import { ResultWizardStep } from '@app/common/components/configurable-wizards/ResultWizardStep/ResultWizardStep';
import { PageWithHeader } from '@app/common/components/PageWithHeader/PageWithHeader';
import { withWizardHeartbeat } from '@app/common/configurable-wizards/hocs/withWizardHeartbeat';
import { useWizardResultRouterUrlState } from '@app/common/configurable-wizards/hooks/useWizardResultRouterUrlState';

import { useFISettingsContext } from '@app/core/contexts/fiSettings/FISettingsContext';
import { PageURL } from '@app/core/widgets/pages';

import { useUserLoginTranslation } from './hooks/useUserLoginTranslation';
import { WizardSteps } from './LoginWizard/constants';
import { QuickLoginSetupWizardStep } from './LoginWizard/steps/QuickLogin/QuickLoginSetupWizardStep';

import { LoginWizard } from './LoginWizard';

/*
Login wizard consists of 4 wizards:
  1. User login
  2. Converted user enrollment
  4. User kind selector.

The usage of those wizards can be mixed in peculiar ways.
For instance, user kind selector consists of a single step and is
used between login and retrieval wizards, when a user clicks on 'I forgot
my password/username.' links.

During a wizard execution the state is stored in two places:
  1. Page URL in form :wizardName/:stepId
  2. Currently opened form.
The progress and is stored in DS session and is none of our business.
*/

const ConfigurableWizard: React.FC = () => {
  const { t } = useUserLoginTranslation();
  const stepPath = (url: string) => `${url}/:stepId?`;
  const wizardState = useWizardResultRouterUrlState();
  const history = useHistory();
  const wizardResultDescriptionId = uniqueId('wizardResultDescription');
  const fiSettings = useFISettingsContext();
  const supportUrl = fiSettings?.getIfConfigured('customerHelpCenterUrl');

  let resultWizardStepTitle: string;
  if (wizardState) {
    resultWizardStepTitle = wizardState.isSuccess
      ? t('login.success-result.title|Login wizard congratulation step dialog title',
        'Congratulations!')
      : t('login.failure-result.title|Login wizard error dialog title',
        'Login process cannot be completed');
  }

  return (
    <PageWithHeader>
      <Switch>
        {wizardState && (
          <Route exact path={`${PageURL.USER_LOGIN}/${WizardSteps.LoginResult}`}>
            <ResultWizardStep
              level={wizardState.isSuccess ? 'success' : 'error'}
              title={resultWizardStepTitle!}
              buttonText={
                t('login.result.return-to-login-button|Login wizard - return to login button text',
                  'Return to login')
              }
              // eslint-disable-next-line i18next/no-literal-string
              buttonStableName="ReturnToLoginButton"
              handleButtonClick={() => {
                history.replace(`${PageURL.USER_LOGIN}`);
                return true;
              }}
            >
              {!wizardState.isSuccess ? (
                <span id={wizardResultDescriptionId}>
                  {/* eslint-disable-next-line i18next/no-literal-string */}
                  {wizardState.message}
                  &nbsp;
                  {
                    t('login.failure-result.contact-us-link|Contact us link text',
                      '<a {{supportHref}} {{supportOptions}}>Contact us</a> for help.',
                      {
                        supportHref: supportUrl ? `href=${supportUrl}` : '',
                        supportOptions: `target="_blank"
                        aria-describedby=${wizardResultDescriptionId} rel="noopener noreferrer nofollow"`,
                      })
                  }
                </span>
              ) : (
                wizardState.message
              )}
            </ResultWizardStep>
          </Route>
        )}
        {/* TODO: remove obsolete tethered login approach */}
        <Route exact path={`${PageURL.USER_LOGIN}/tethered/:token`}>
          <LoginWizard />
        </Route>
        <Route exact path={`${PageURL.USER_LOGIN}/${WizardSteps.QuickLoginSetup}`}>
          <QuickLoginSetupWizardStep />
        </Route>
        <Route path={stepPath(PageURL.USER_LOGIN)}>
          <LoginWizard />
        </Route>
      </Switch>
    </PageWithHeader>
  );
};

export default withWizardHeartbeat(ConfigurableWizard);
